import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Briefcase, Quote, Sparkles } from "lucide-react";
import Navbar from "@/components/sections/Navbar";
import Footer from "@/components/sections/Footer";

export default function PortfolioPage() {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <main
            data-testid="portfolio-page"
            className="relative min-h-screen text-white font-body antialiased"
        >
            <Navbar />

            {/* Hero */}
            <section className="relative pt-36 pb-16 luxe-radial overflow-hidden">
                <div className="absolute inset-0 grain pointer-events-none" />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <button
                        data-testid="portfolio-home-btn-top"
                        onClick={() => navigate("/")}
                        className="group inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#C4C4C4] hover:text-[#D4AF37] transition-colors duration-300 mb-12"
                    >
                        <Home className="w-3.5 h-3.5" strokeWidth={1.5} />
                        Return Home
                    </button>

                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            Project &amp; Testimonials
                        </span>
                    </div>

                    <h1
                        data-testid="portfolio-headline"
                        className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[88px] leading-[1.02] tracking-tight text-white font-light max-w-5xl"
                    >
                        Our finest work.
                        <br />
                        <span className="italic text-gold-metallic">
                            Coming into view.
                        </span>
                    </h1>

                    <p className="mt-8 max-w-2xl text-base md:text-lg font-light leading-relaxed text-[#C4C4C4]">
                        A curated showcase of client launches and the voices behind
                        them is on the way. We&apos;re quietly polishing every case
                        study and testimonial so this page reflects the same
                        standard as the work itself.
                    </p>
                </div>
            </section>

            {/* Portfolio section */}
            <section
                data-testid="portfolio-section"
                className="relative py-24 md:py-32 luxe-radial-faint"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            01 · Portfolio
                        </span>
                    </div>

                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white font-light max-w-4xl">
                        A gallery of launches,{" "}
                        <span className="italic text-gold-metallic">
                            in the making.
                        </span>
                    </h2>

                    <div
                        data-testid="portfolio-placeholder"
                        className="mt-14 relative border border-[#D4AF37]/25 bg-black/40 backdrop-blur-sm overflow-hidden"
                    >
                        <div className="absolute inset-0 pointer-events-none opacity-40">
                            <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                            <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                        </div>
                        <div className="relative px-8 py-20 md:py-28 text-center">
                            <Briefcase
                                className="w-10 h-10 text-[#D4AF37] mx-auto mb-6"
                                strokeWidth={1.25}
                            />
                            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">
                                Now Curating
                            </div>
                            <h3 className="font-display text-3xl md:text-4xl text-white mb-5">
                                Case studies coming soon.
                            </h3>
                            <p className="text-sm md:text-base font-light leading-relaxed text-[#C4C4C4] max-w-2xl mx-auto">
                                We&apos;re assembling a select gallery of recent
                                RecoveryDesk launches — each with the brief, the
                                build, and the measurable results. Check back
                                shortly, or reach out and we&apos;ll walk you
                                through examples personally.
                            </p>
                            <button
                                data-testid="portfolio-contact-cta"
                                onClick={() => navigate("/#contact")}
                                className="mt-10 inline-flex items-center gap-3 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] hover:border-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                            >
                                <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                                Request Recent Work
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials section */}
            <section
                data-testid="testimonials-section"
                className="relative py-24 md:py-32 luxe-radial-soft"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="gold-rule-solid w-10" />
                        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] font-semibold">
                            02 · Testimonials
                        </span>
                    </div>

                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-white font-light max-w-4xl">
                        Words from the people we&apos;ve{" "}
                        <span className="italic text-gold-metallic">
                            built for.
                        </span>
                    </h2>

                    <div
                        data-testid="testimonials-placeholder"
                        className="mt-14 relative border border-[#D4AF37]/25 bg-black/40 backdrop-blur-sm overflow-hidden"
                    >
                        <div className="absolute inset-0 pointer-events-none opacity-40">
                            <div className="absolute -top-24 left-1/3 w-96 h-96 rounded-full bg-[#D4AF37]/10 blur-3xl" />
                        </div>
                        <div className="relative px-8 py-20 md:py-28 text-center">
                            <Quote
                                className="w-10 h-10 text-[#D4AF37] mx-auto mb-6"
                                strokeWidth={1.25}
                            />
                            <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">
                                Now Gathering
                            </div>
                            <h3 className="font-display text-3xl md:text-4xl text-white mb-5">
                                Client voices arriving shortly.
                            </h3>
                            <p className="text-sm md:text-base font-light leading-relaxed text-[#C4C4C4] max-w-2xl mx-auto">
                                Our clients are still enjoying their new sites — we
                                like to give them room to see the results before
                                asking for a word. Genuine testimonials will land
                                here soon.
                            </p>
                            <button
                                data-testid="testimonials-contact-cta"
                                onClick={() => navigate("/#contact")}
                                className="mt-10 inline-flex items-center gap-3 bg-[#D4AF37] text-black px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold btn-gold-hover hover:bg-[#E5C158]"
                            >
                                Start Your Project
                            </button>
                        </div>
                    </div>

                    <div className="mt-20 flex justify-center">
                        <button
                            data-testid="portfolio-home-btn-bottom"
                            onClick={() => navigate("/")}
                            className="group inline-flex items-center gap-3 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/[0.08] hover:border-[#D4AF37] px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-all duration-300"
                        >
                            <Home className="w-4 h-4" strokeWidth={1.5} />
                            Return Home
                        </button>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
